/**
 * Module 3.3 - Registration > Representation
 * Shared constants and options for all entities
 * I18n-aware options with {pt, en, es} labels
 */

export interface I18nLabel {
    pt: string;
    en: string;
    es: string;
}

export interface I18nOption<T = string> {
    label: I18nLabel;
    value: T;
}

/** Helper to build a tuple list from I18nOption[] for backward compat */
const toTuples = (options: I18nOption[], lang: 'pt' | 'en' | 'es' = 'pt'): [string, string][] =>
    options.map((o) => [o.value, o.label[lang]]);

// ---------------------------------------------------------------------------
// Representation Type Options
// ---------------------------------------------------------------------------
export const representationTypeI18nOptions: I18nOption[] = [
    { label: { pt: 'Procuração Geral', en: 'General Power of Attorney', es: 'Poder General' }, value: 'PROCURACAO_GERAL' },
    { label: { pt: 'Procuração Específica', en: 'Specific Power of Attorney', es: 'Poder Especifico' }, value: 'PROCURACAO_ESPECIFICA' },
    { label: { pt: 'Representação Legal', en: 'Legal Representation', es: 'Representacion Legal' }, value: 'REPRESENTACAO_LEGAL' },
    { label: { pt: 'Tutela', en: 'Guardianship', es: 'Tutela' }, value: 'TUTELA' },
    { label: { pt: 'Curatela', en: 'Curatorship', es: 'Curatela' }, value: 'CURATELA' },
    { label: { pt: 'Mandato Empresarial', en: 'Corporate Mandate', es: 'Mandato Empresarial' }, value: 'MANDATO_EMPRESARIAL' },
    { label: { pt: 'Delegação Administrativa', en: 'Administrative Delegation', es: 'Delegacion Administrativa' }, value: 'DELEGACAO_ADMINISTRATIVA' },
];

// ---------------------------------------------------------------------------
// Public Agency Representation Type Options
// ---------------------------------------------------------------------------
export const publicAgencyRepresentationTypeI18nOptions: I18nOption[] = [
    { label: { pt: 'Equipe de Apoio', en: 'Support Team', es: 'Equipo de Apoyo' }, value: 'EQUIPE_APOIO' },
    { label: { pt: 'Pregoeiro/Agente de Contratação', en: 'Auctioneer/Procurement Agent', es: 'Pregoeiro/Agente de Contratación' }, value: 'PREGOEIRO' },
    { label: { pt: 'Autoridade Superior', en: 'Senior Authority', es: 'Autoridad Superior' }, value: 'AUTORIDADE_SUPERIOR' },
];

// ---------------------------------------------------------------------------
// Representation Status Options
// ---------------------------------------------------------------------------
export const representationStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Ativa', en: 'Active', es: 'Activa' }, value: 'ACTIVE' },
    { label: { pt: 'Suspensa', en: 'Suspended', es: 'Suspendida' }, value: 'SUSPENDED' },
    { label: { pt: 'Revogada', en: 'Revoked', es: 'Revocada' }, value: 'REVOKED' },
    { label: { pt: 'Expirada', en: 'Expired', es: 'Expirada' }, value: 'EXPIRED' },
    { label: { pt: 'Pendente de Validacao', en: 'Pending Validation', es: 'Pendiente de Validacion' }, value: 'PENDING_VALIDATION' },
];

// ---------------------------------------------------------------------------
// Granted Powers Options
// ---------------------------------------------------------------------------
export const grantedPowersI18nOptions: I18nOption[] = [
    { label: { pt: 'Assinar Contratos', en: 'Sign Contracts', es: 'Firmar Contratos' }, value: 'ASSINAR_CONTRATOS' },
    { label: { pt: 'Movimentar Financeiro', en: 'Financial Transactions', es: 'Transacciones Financieras' }, value: 'MOVIMENTAR_FINANCEIRO' },
    { label: { pt: 'Representar Juridicamente', en: 'Legal Representation', es: 'Representar Juridicamente' }, value: 'REPRESENTAR_JURIDICAMENTE' },
    { label: { pt: 'Participar de Licitacoes', en: 'Participate in Tenders', es: 'Participar en Licitaciones' }, value: 'PARTICIPAR_LICITACOES' },
    { label: { pt: 'Gerenciar Documentos', en: 'Manage Documents', es: 'Gestionar Documentos' }, value: 'GERENCIAR_DOCUMENTOS' },
    { label: { pt: 'Acesso a Dados Pessoais', en: 'Personal Data Access', es: 'Acceso a Datos Personales' }, value: 'ACESSO_DADOS_PESSOAIS' },
    { label: { pt: 'Tomar Decisoes Administrativas', en: 'Administrative Decisions', es: 'Decisiones Administrativas' }, value: 'TOMAR_DECISOES_ADMINISTRATIVAS' },
    { label: { pt: 'Delegar a Terceiros', en: 'Delegate to Third Parties', es: 'Delegar a Terceros' }, value: 'DELEGAR_TERCEIROS' },
];

// ---------------------------------------------------------------------------
// Representative Profile Status Options
// ---------------------------------------------------------------------------
export const profileStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Ativo', en: 'Active', es: 'Activo' }, value: 'ACTIVE' },
    { label: { pt: 'Inativo', en: 'Inactive', es: 'Inactivo' }, value: 'INACTIVE' },
    { label: { pt: 'Suspenso', en: 'Suspended', es: 'Suspendido' }, value: 'SUSPENDED' },
    { label: { pt: 'Em Verificacao', en: 'Under Verification', es: 'En Verificacion' }, value: 'UNDER_VERIFICATION' },
    { label: { pt: 'Bloqueado', en: 'Blocked', es: 'Bloqueado' }, value: 'BLOCKED' },
];

// ---------------------------------------------------------------------------
// Primary Representation Type (for profiles)
// ---------------------------------------------------------------------------
export const primaryRepresentationTypeI18nOptions: I18nOption[] = [
    { label: { pt: 'Advogado', en: 'Lawyer', es: 'Abogado' }, value: 'ADVOGADO' },
    { label: { pt: 'Procurador', en: 'Attorney', es: 'Procurador' }, value: 'PROCURADOR' },
    { label: { pt: 'Representante Legal', en: 'Legal Representative', es: 'Representante Legal' }, value: 'REPRESENTANTE_LEGAL' },
    { label: { pt: 'Tutor', en: 'Guardian', es: 'Tutor' }, value: 'TUTOR' },
    { label: { pt: 'Curador', en: 'Curator', es: 'Curador' }, value: 'CURADOR' },
    { label: { pt: 'Administrador', en: 'Administrator', es: 'Administrador' }, value: 'ADMINISTRADOR' },
    { label: { pt: 'Mandatario', en: 'Mandatory', es: 'Mandatario' }, value: 'MANDATARIO' },
    { label: { pt: 'Agente Comercial', en: 'Commercial Agent', es: 'Agente Comercial' }, value: 'AGENTE_COMERCIAL' },
];

// ---------------------------------------------------------------------------
// Specializations Options
// ---------------------------------------------------------------------------
export const specializationsI18nOptions: I18nOption[] = [
    { label: { pt: 'Direito Civil', en: 'Civil Law', es: 'Derecho Civil' }, value: 'DIREITO_CIVIL' },
    { label: { pt: 'Direito Empresarial', en: 'Business Law', es: 'Derecho Empresarial' }, value: 'DIREITO_EMPRESARIAL' },
    { label: { pt: 'Direito Administrativo', en: 'Administrative Law', es: 'Derecho Administrativo' }, value: 'DIREITO_ADMINISTRATIVO' },
    { label: { pt: 'Licitacoes e Contratos', en: 'Tenders and Contracts', es: 'Licitaciones y Contratos' }, value: 'LICITACOES_CONTRATOS' },
    { label: { pt: 'Direito Tributario', en: 'Tax Law', es: 'Derecho Tributario' }, value: 'DIREITO_TRIBUTARIO' },
    { label: { pt: 'Direito Trabalhista', en: 'Labor Law', es: 'Derecho Laboral' }, value: 'DIREITO_TRABALHISTA' },
    { label: { pt: 'Procuracoes Gerais', en: 'General Powers of Attorney', es: 'Poderes Generales' }, value: 'PROCURACOES_GERAIS' },
    { label: { pt: 'Representacao Comercial', en: 'Commercial Representation', es: 'Representacion Comercial' }, value: 'REPRESENTACAO_COMERCIAL' },
    { label: { pt: 'Administracao de Bens', en: 'Asset Management', es: 'Administracion de Bienes' }, value: 'ADMINISTRACAO_BENS' },
];

// ---------------------------------------------------------------------------
// Qualification Level Options
// ---------------------------------------------------------------------------
export const qualificationLevelI18nOptions: I18nOption[] = [
    { label: { pt: 'Junior', en: 'Junior', es: 'Junior' }, value: 'JUNIOR' },
    { label: { pt: 'Pleno', en: 'Mid-Level', es: 'Pleno' }, value: 'PLENO' },
    { label: { pt: 'Senior', en: 'Senior', es: 'Senior' }, value: 'SENIOR' },
    { label: { pt: 'Especialista', en: 'Specialist', es: 'Especialista' }, value: 'ESPECIALISTA' },
];

// ---------------------------------------------------------------------------
// Verification Status Options
// ---------------------------------------------------------------------------
export const verificationStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Nao Verificado', en: 'Unverified', es: 'No Verificado' }, value: 'NOT_VERIFIED' },
    { label: { pt: 'Em Verificacao', en: 'Under Verification', es: 'En Verificacion' }, value: 'UNDER_VERIFICATION' },
    { label: { pt: 'Verificado', en: 'Verified', es: 'Verificado' }, value: 'VERIFIED' },
    { label: { pt: 'Verificacao Expirada', en: 'Verification Expired', es: 'Verificacion Expirada' }, value: 'VERIFICATION_EXPIRED' },
];

// ---------------------------------------------------------------------------
// Request Status Options
// ---------------------------------------------------------------------------
export const requestStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Pendente', en: 'Pending', es: 'Pendiente' }, value: 'PENDING' },
    { label: { pt: 'Aprovada', en: 'Approved', es: 'Aprobada' }, value: 'APPROVED' },
    { label: { pt: 'Rejeitada', en: 'Rejected', es: 'Rechazada' }, value: 'REJECTED' },
    { label: { pt: 'Cancelada', en: 'Cancelled', es: 'Cancelada' }, value: 'CANCELLED' },
    { label: { pt: 'Em Analise', en: 'Under Analysis', es: 'En Analisis' }, value: 'UNDER_ANALYSIS' },
    { label: { pt: 'Aguardando Documentacao', en: 'Awaiting Documentation', es: 'Esperando Documentacion' }, value: 'AWAITING_DOCUMENTATION' },
];

// ---------------------------------------------------------------------------
// Urgency Level Options
// ---------------------------------------------------------------------------
export const urgencyLevelI18nOptions: I18nOption[] = [
    { label: { pt: 'Baixa', en: 'Low', es: 'Baja' }, value: 'BAIXA' },
    { label: { pt: 'Media', en: 'Medium', es: 'Media' }, value: 'MEDIA' },
    { label: { pt: 'Alta', en: 'High', es: 'Alta' }, value: 'ALTA' },
    { label: { pt: 'Critica', en: 'Critical', es: 'Critica' }, value: 'CRITICA' },
];

// ---------------------------------------------------------------------------
// Authority Level Options
// ---------------------------------------------------------------------------
export const authorityLevelI18nOptions: I18nOption[] = [
    { label: { pt: 'Baixo', en: 'Low', es: 'Bajo' }, value: 'BAIXO' },
    { label: { pt: 'Medio', en: 'Medium', es: 'Medio' }, value: 'MEDIO' },
    { label: { pt: 'Alto', en: 'High', es: 'Alto' }, value: 'ALTO' },
    { label: { pt: 'Critico', en: 'Critical', es: 'Critico' }, value: 'CRITICO' },
];

// ---------------------------------------------------------------------------
// Approval Level Options
// ---------------------------------------------------------------------------
export const approvalLevelI18nOptions: I18nOption[] = [
    { label: { pt: 'Gerencial', en: 'Managerial', es: 'Gerencial' }, value: 'GERENCIAL' },
    { label: { pt: 'Diretoria', en: 'Board', es: 'Directiva' }, value: 'DIRETORIA' },
    { label: { pt: 'Juridico', en: 'Legal', es: 'Juridico' }, value: 'JURIDICO' },
    { label: { pt: 'Compliance', en: 'Compliance', es: 'Compliance' }, value: 'COMPLIANCE' },
];

// ---------------------------------------------------------------------------
// Category Status Options
// ---------------------------------------------------------------------------
export const categoryStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Ativa', en: 'Active', es: 'Activa' }, value: 'ACTIVE' },
    { label: { pt: 'Inativa', en: 'Inactive', es: 'Inactiva' }, value: 'INACTIVE' },
    { label: { pt: 'Descontinuada', en: 'Discontinued', es: 'Descontinuada' }, value: 'DESCONTINUADA' },
];

// ---------------------------------------------------------------------------
// Document Validation Status Options
// ---------------------------------------------------------------------------
export const documentValidationStatusI18nOptions: I18nOption[] = [
    { label: { pt: 'Nao Verificado', en: 'Unverified', es: 'No Verificado' }, value: 'NOT_VERIFIED' },
    { label: { pt: 'Em Analise', en: 'Under Analysis', es: 'En Analisis' }, value: 'UNDER_ANALYSIS' },
    { label: { pt: 'Aprovado', en: 'Approved', es: 'Aprobado' }, value: 'APPROVED' },
    { label: { pt: 'Rejeitado', en: 'Rejected', es: 'Rechazado' }, value: 'REJECTED' },
    { label: { pt: 'Incompleto', en: 'Incomplete', es: 'Incompleto' }, value: 'INCOMPLETE' },
];

// ---------------------------------------------------------------------------
// Backwards-compatible tuple exports (deprecated - use I18nOption variants)
// ---------------------------------------------------------------------------
/** @deprecated Use representationTypeI18nOptions */
export const REPRESENTATION_TYPE_OPTIONS: [string, string][] = toTuples(representationTypeI18nOptions);
/** @deprecated Use publicAgencyRepresentationTypeI18nOptions */
export const PUBLIC_AGENCY_REPRESENTATION_TYPE_OPTIONS: [string, string][] = toTuples(publicAgencyRepresentationTypeI18nOptions);
/** @deprecated Use representationStatusI18nOptions */
export const REPRESENTATION_STATUS_OPTIONS: [string, string][] = toTuples(representationStatusI18nOptions);
/** @deprecated Use grantedPowersI18nOptions */
export const GRANTED_POWERS_OPTIONS: [string, string][] = toTuples(grantedPowersI18nOptions);
/** @deprecated Use profileStatusI18nOptions */
export const PROFILE_STATUS_OPTIONS: [string, string][] = toTuples(profileStatusI18nOptions);
/** @deprecated Use primaryRepresentationTypeI18nOptions */
export const PRIMARY_REPRESENTATION_TYPE_OPTIONS: [string, string][] = toTuples(primaryRepresentationTypeI18nOptions);
/** @deprecated Use specializationsI18nOptions */
export const SPECIALIZATIONS_OPTIONS: [string, string][] = toTuples(specializationsI18nOptions);
/** @deprecated Use qualificationLevelI18nOptions */
export const QUALIFICATION_LEVEL_OPTIONS: [string, string][] = toTuples(qualificationLevelI18nOptions);
/** @deprecated Use verificationStatusI18nOptions */
export const VERIFICATION_STATUS_OPTIONS: [string, string][] = toTuples(verificationStatusI18nOptions);
/** @deprecated Use requestStatusI18nOptions */
export const REQUEST_STATUS_OPTIONS: [string, string][] = toTuples(requestStatusI18nOptions);
/** @deprecated Use urgencyLevelI18nOptions */
export const URGENCY_LEVEL_OPTIONS: [string, string][] = toTuples(urgencyLevelI18nOptions);
/** @deprecated Use authorityLevelI18nOptions */
export const AUTHORITY_LEVEL_OPTIONS: [string, string][] = toTuples(authorityLevelI18nOptions);
/** @deprecated Use approvalLevelI18nOptions */
export const APPROVAL_LEVEL_OPTIONS: [string, string][] = toTuples(approvalLevelI18nOptions);
/** @deprecated Use categoryStatusI18nOptions */
export const CATEGORY_STATUS_OPTIONS: [string, string][] = toTuples(categoryStatusI18nOptions);
/** @deprecated Use documentValidationStatusI18nOptions */
export const DOCUMENT_VALIDATION_STATUS_OPTIONS: [string, string][] = toTuples(documentValidationStatusI18nOptions);
